import React, { useMemo, useState, useEffect } from "react";

import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { Collapse } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useTranslate } from "../../hooks/useTranslate";
import Icon from "../Icon";
import joinClassNames from "../../helpers/joinClassNames";
import TemplateLink from "./TemplateLink";
import { useService } from "../../hooks/useService";
import {
  APP_GROUP_LINKS,
  SIDEBAR_SERVICE_MAP,
} from "../../../groups/app/config";
import { SIDEBAR_ITEMS_TYPE } from "./Sidebar";
import { KEY_USER } from "../../constants/storage";
import StorageService from "../../../services/StorageService";
import { USER_ROLES } from "../../constants/shared";

const ACTIVE_LINK = "waves-effect active mm-active";
const ARROW = "has-arrow";
const CONDENSED = "condensed";

export default function generateSidebarLinks(linksArray, sidebarType) {
  /**
   * @type {StorageService}
   */
  const storage = useService(StorageService);

  const [cachedServicesSettings, setCachedServicesSettings] = useState(() =>
    storage.get("system:servicesSettings", {})
  );

  useEffect(() => {
    let intervalId;
    function checkSettings() {
      const settings = storage.get("system:servicesSettings", {});
      if (settings && Object.keys(settings).length > 0) {
        setCachedServicesSettings(settings);
        clearInterval(intervalId);
      }
    }
    if (
      !cachedServicesSettings ||
      Object.keys(cachedServicesSettings).length === 0
    ) {
      intervalId = setInterval(checkSettings, 500);
    }
    return () => clearInterval(intervalId);
  }, [storage]);

  const location = useLocation();
  const currentLocation = useMemo(() => location.pathname, [location]);
  const navigate = useNavigate();
  const user = storage.get(KEY_USER, { firstName: "", lastName: "" });
  const userPermissions = user?.adminRole?.permissions || [];

  const hasPermission = (label) => {
    if (!userPermissions || userPermissions.length === 0) {
      return true;
    }

    const serviceNames = SIDEBAR_SERVICE_MAP[label];
    if (!serviceNames) return true;

    const servicesToCheck = Array.isArray(serviceNames)
      ? serviceNames
      : [serviceNames];
    return servicesToCheck.some((serviceName) =>
      userPermissions.some(
        (permission) => permission.organisationServiceName === serviceName
      )
    );
  };

  const isCondensed = sidebarType === CONDENSED;
  const organisationsSideBar = "SIDEBAR.ORGANISATIONS";
  const DEFAULT_OPTIMALLY_ME_ORG = 1;
  const { organisationId } = storage.get(KEY_USER, {
    organisationId: DEFAULT_OPTIMALLY_ME_ORG,
  });

  const [translate] = useTranslate();
  const [activeLink, setActiveLink] = useState(currentLocation);

  function isSidebarLinkVisible(child) {
    if (child.label && cachedServicesSettings) {
      const serviceNames = SIDEBAR_SERVICE_MAP[child.label];
      if (serviceNames) {
        const servicesToCheck = Array.isArray(serviceNames)
          ? serviceNames
          : [serviceNames];
        const isAnyEnabled = servicesToCheck.some(
          (serviceName) => cachedServicesSettings[serviceName]
        );
        if (!isAnyEnabled) {
          return false;
        }
      }
    }

    if (
      child.label == organisationsSideBar &&
      !(
        user.role == USER_ROLES.superAdmin &&
        organisationId == DEFAULT_OPTIMALLY_ME_ORG
      )
    ) {
      return false;
    }

    if (user.role === USER_ROLES.practitioner) {
      if (!child.allowedRoles) {
        return false;
      }
      if (!child.allowedRoles.includes(user.role)) {
        return false;
      }
    }

    if (child.label && !hasPermission(child.label)) {
      return false;
    }

    return true;
  }

  function getAllVisibleLinks(links) {
    let result = [];
    for (let i = 0; i < links.length; i++) {
      const child = links[i];
      if (!isSidebarLinkVisible(child)) {
        continue;
      }

      if (!child.type && child.linkTo) {
        result.push(child.linkTo);
      }

      if (child.children?.length) {
        result = result.concat(getAllVisibleLinks(child.children));
      }
    }
    return result;
  }

  useEffect(() => {
    if (
      !cachedServicesSettings ||
      Object.keys(cachedServicesSettings).length === 0
    ) {
      return;
    }
    const shouldRedirect = storage.get("updateAdminSidebar");
    if (!shouldRedirect) {
      return;
    }
    const visibleLinks = getAllVisibleLinks(linksArray);
    if (
      visibleLinks.length > 0 &&
      !visibleLinks.includes(currentLocation)
    ) {
      navigate(visibleLinks[0], { replace: true });
    }
    storage.set("updateAdminSidebar", false);
  }, [
    cachedServicesSettings,
    currentLocation,
    linksArray,
    navigate,
    SIDEBAR_SERVICE_MAP,
    organisationsSideBar,
    user,
    organisationId,
    storage
  ]);

  if (!cachedServicesSettings) {
    return null;
  }

  function getLinkClassName(link, arrow = "") {
    if (currentLocation.includes(link)) {
      return `${ACTIVE_LINK} ${arrow}`;
    }
    return `waves-effect ${arrow}`;
  }

  function isOpenedCollapse(link) {
    if (link?.length > activeLink?.length) {
      return;
    }
    return activeLink?.startsWith(link);
  }

  const toggleCollapse = (link, parentLink) => {
    setActiveLink((prevState) => {
      return prevState === link ? parentLink : link;
    });
  };

  function getLinksFloor(children, parentLink) {
    const visibleItems = [];

    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (!isSidebarLinkVisible(child)) {
        continue;
      }

      const hasChilds = !!child.children?.length;
      const filteredChildren = hasChilds
        ? child.children.filter((subChild) => {
            if (subChild.label && cachedServicesSettings) {
              const serviceNames = SIDEBAR_SERVICE_MAP[subChild.label];
              if (serviceNames) {
                const servicesToCheck = Array.isArray(serviceNames)
                  ? serviceNames
                  : [serviceNames];
                const isAnyEnabled = servicesToCheck.some(
                  (serviceName) => cachedServicesSettings[serviceName]
                );
                if (!isAnyEnabled) {
                  return false;
                }
              }
            }
            return subChild.label ? hasPermission(subChild.label) : true;
          })
        : [];

      if (hasChilds && filteredChildren.length === 0) {
        continue;
      }

      visibleItems.push({ child, index: i, filteredChildren });
    }

    const finalItems = [];

    for (let i = 0; i < visibleItems.length; i++) {
      const { child, index, filteredChildren } = visibleItems[i];

      if (child.type === SIDEBAR_ITEMS_TYPE.DIVIDER) {
        let hasContentBefore = false;
        let hasContentAfter = false;

        for (let j = i - 1; j >= 0; j--) {
          const prevItem = visibleItems[j].child;
          if (prevItem.type === SIDEBAR_ITEMS_TYPE.DIVIDER) {
            break;
          }
          if (prevItem.type !== SIDEBAR_ITEMS_TYPE.TITLE) {
            hasContentBefore = true;
            break;
          }
        }

        for (let j = i + 1; j < visibleItems.length; j++) {
          const nextItem = visibleItems[j].child;
          if (nextItem.type === SIDEBAR_ITEMS_TYPE.DIVIDER) {
            break;
          }
          if (nextItem.type !== SIDEBAR_ITEMS_TYPE.TITLE) {
            hasContentAfter = true;
            break;
          }
        }

        if (hasContentBefore && hasContentAfter) {
          finalItems.push({ child, index, filteredChildren });
        }
      } else if (child.type === SIDEBAR_ITEMS_TYPE.TITLE) {
        let hasContentAfter = false;
        for (let j = i + 1; j < visibleItems.length; j++) {
          const nextItem = visibleItems[j].child;
          if (
            nextItem.type === SIDEBAR_ITEMS_TYPE.DIVIDER ||
            nextItem.type === SIDEBAR_ITEMS_TYPE.TITLE
          ) {
            break;
          }
          hasContentAfter = true;
          break;
        }

        if (hasContentAfter) {
          finalItems.push({ child, index, filteredChildren });
        }
      } else {
        finalItems.push({ child, index, filteredChildren });
      }
    }

    return finalItems.map(({ child, index, filteredChildren }) => {
      const hasChilds = !!child.children?.length;

      if (child.type === SIDEBAR_ITEMS_TYPE.DIVIDER) {
        return (
          <div
            key={`divider-${index}`}
            className="border-1 border border-secondary my-3 w-100"
          />
        );
      }

      if (child.type === SIDEBAR_ITEMS_TYPE.TITLE) {
        return (
          <li key={`title-${index}`} className="sidebar-title">
            {translate(child.label)}
          </li>
        );
      }

      return (
        <li key={child.linkTo}>
          <a
            className={joinClassNames(
              hasChilds || child.templates?.length > 0
                ? getLinkClassName(child.linkTo, ARROW)
                : getLinkClassName(child.linkTo),
              "d-flex align-items-center w-100"
            )}
            onClick={(event) => {
              event.preventDefault();
              if (hasChilds) {
                toggleCollapse(child.linkTo, parentLink);
              } else {
                setActiveLink(child.linkTo);
                navigate(child.linkTo);
              }
              event.stopPropagation();
            }}
            href={child.linkTo}
          >
            {child.icon && (
              <Icon
                icon={child.icon}
                className={joinClassNames(
                  "link-icon",
                  activeLink?.includes(child.linkTo) && "active"
                )}
              />
            )}
            <span>{translate(child.label)}</span>
          </a>
          {hasChilds &&
            (!isCondensed ? (
              <Collapse isOpen={isOpenedCollapse(child.linkTo)}>
                <ul className="sub-menu">
                  {getLinksFloor(filteredChildren, child.linkTo)}
                </ul>
              </Collapse>
            ) : (
              <ul className="sub-menu">
                {getLinksFloor(filteredChildren, child.linkTo)}
              </ul>
            ))}

          {!!child.templates?.length && (() => {
            const shouldBeOpen = isOpenedCollapse(child.linkTo);
            console.log('Templates Debug:', {
              linkTo: child.linkTo,
              activeLink,
              templatesLength: child.templates.length,
              shouldBeOpen,
              templates: child.templates
            });
            return (
              <Collapse isOpen={shouldBeOpen}>
                <label className="sidebar-templates__title">
                  Favorite templates:
                </label>
                <ul className="sub-menu sidebar-templates custom-scrollbar me-2">
                  {child.templates.map((template) => {
                    return (
                      <li key={template?.id?.toString()}>
                        <TemplateLink
                          template={template}
                          setActiveLinkToCustomerResults={
                            setActiveLinkToCustomerResults
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              </Collapse>
            );
          })()}
        </li>
      );
    });
  }

  const setActiveLinkToCustomerResults = () => {
    setActiveLink(APP_GROUP_LINKS.CUSTOMER_RESULTS);
  };

  return (
    <SimpleBar style={{ height: "calc(100vh - 70px)" }}>
      <div id="sidebar-menu">
        <ul className="metismenu list-unstyled custom-sidebar-menu">
          {getLinksFloor(linksArray)}
        </ul>
      </div>
      <div id="sidebar-footer">
        <div className="powered-by-label">Powered by</div>
        <div className="powered-by-logo">
          <span className="logo-lg">
            <Icon icon="sidebarLogo" />
          </span>
        </div>
      </div>
    </SimpleBar>
  );
}

generateSidebarLinks.propTypes = {
  linksArray: PropTypes.array,
};
